<template>
    <settings-wrapper title="User settings" section="Variables">
        <div class="d-flex">
            <v-alert type="info" text dense>Changes will take effect after the application is restarted.</v-alert>
        </div>
        <v-skeleton-loader v-if="requestRunning === 'get'" class="mx-auto" type="table"></v-skeleton-loader>
        <template v-else>
            <div class="d-flex align-center">
                <env-var-create-dialog v-if="canAddEnvVar()" :level="level" @created="fetchEnvVars">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn class="mr-2" color="primary" v-bind="attrs" v-on="on">
                            <v-icon left>mdi-plus</v-icon>
                            <span>Variable</span>
                        </v-btn>
                    </template>
                </env-var-create-dialog>
                <v-spacer></v-spacer>
                <v-text-field
                    placeholder="Search for an environment variable..."
                    autofocus
                    v-model="search"
                    hide-details
                    prepend-inner-icon="mdi-magnify "
                    solo
                    flat
                    dense
                    clearable
                    class="mr-2" />
                <v-btn @click="fetchEnvVars()" icon><v-icon>refresh</v-icon></v-btn>
            </div>
            <v-data-table
                :items="envvars"
                item-key="evid"
                :headers="headers"
                :search="search"
                :options="tableOptions"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                class="mt-4">
                <template v-slot:top="{ pagination, options, updateOptions }">
                    <v-data-footer
                        :pagination="pagination"
                        :options="options"
                        @update:options="updateOptions"
                        :itemsPerPageOptions="itemsPerPageOptions"
                        items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                        class="no-border" />
                </template>
                <template v-slot:no-data>
                    <span>No environment variables have been added, yet. Click add new variable to create a new one.</span>
                </template>
                <template v-slot:[`item.name`]="{ item }">
                    <code class="text-body-1">{{ item.name }}</code>
                    <v-tooltip bottom v-if="item.overrides_secret_type">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon color="primary" small v-bind="attrs" v-on="on" class="ml-3">mdi-information-outline</v-icon>
                        </template>
                        <span>Overrides {{ secretLevels[item.overrides_secret_type].toLocaleLowerCase() }} secret</span>
                    </v-tooltip>
                    <v-tooltip bottom v-if="item.env_var_type > level">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon small v-bind="attrs" v-on="on" class="ml-3">mdi-information-outline</v-icon>
                        </template>
                        <span>Defined for the {{ secretLevels[item.env_var_type].toLowerCase() }}</span>
                    </v-tooltip>
                </template>
                <template v-slot:[`item.value`]="{ item }">
                    <code class="text-body-1">{{ item.value }}</code>
                </template>
                <template v-slot:[`item.creator_name`]="{ item }">
                    {{ item.last_editor_name || item.creator_name }}
                </template>
                <template v-slot:[`item.creation_timestamp`]="{ item }">
                    {{ (item.last_modified_timestamp || item.creation_timestamp) | dateTimeToHuman }}
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu left offset-x nudge-top="10">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" text color="secondary" small>
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <copy-to-clipboard :textToCopy="item.name" buttonName="Copy name to clipboard" type="listItem" />
                            <copy-to-clipboard
                                :textToCopy="item.value"
                                buttonName="Copy value to clipboard"
                                type="listItem"
                                icon="mdi-text-box-multiple-outline" />
                            <v-list-item v-if="canModifyEnvVar(item)" @click="openEnvVarDialog(item)">
                                <v-list-item-icon>
                                    <v-progress-circular
                                        v-if="selectedEnvVar?.evid === item.evid && requestRunning === 'patch'"
                                        size="18"
                                        width="2"
                                        indeterminate
                                        color="secondary" />
                                    <v-icon v-else color="secondary">mdi-pencil-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title class="secondary--text">Edit variable</v-list-item-title>
                            </v-list-item>
                            <v-divider v-if="canModifyEnvVar(item)" class="my-2"></v-divider>
                            <v-list-item v-if="canModifyEnvVar(item)" @click="openDeleteDialog(item)">
                                <v-list-item-icon>
                                    <v-progress-circular
                                        v-if="selectedEnvVar?.evid === item.evid && requestRunning === 'delete'"
                                        size="18"
                                        width="2"
                                        indeterminate
                                        color="error" />
                                    <v-icon v-else color="error">mdi-trash-can-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title class="error--text">Delete variable</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </template>
            </v-data-table>
        </template>

        <v-dialog v-model="envVarDialog" max-width="600px" lazy-validation persistent>
            <v-card>
                <v-card-title class="flex-nowrap align-start">
                    <span v-if="selectedEnvVar">Edit {{ selectedEnvVar.name }}</span>

                    <v-spacer></v-spacer>
                    <v-btn @click="envVarDialog = false" icon><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-card-subtitle class="caption">
                    This environment variable will be available in all applications of this {{ secretLevels[level].toLowerCase() }}.
                </v-card-subtitle>
                <v-card-text class="mb-0">
                    <v-form v-model="envVarFormIsValid" @submit.prevent="sendUpdateRequest()">
                        <v-textarea v-model="newEnvVarValue" outlined :rules="[rules.required]" dense hint="This field is required" autofocus>
                            <template v-slot:label>Type a value for your variable</template>
                        </v-textarea>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn text color="primary" @click="sendUpdateRequest()" :disabled="!envVarFormIsValid" :loading="requestRunning === 'patch'">Edit</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </settings-wrapper>
</template>

<script>
import { enumsData } from '@/mixins/enums'
import eventBus from '@/components/EventBus'
import { mapGetters, mapState } from 'vuex'
import SettingsWrapper from '@/components/SettingsWrapper.vue'

const CopyToClipboard = () => import('@/components/CopyToClipboard.vue')
const EnvVarCreateDialog = () => import('@/components/EnvVarCreateDialog.vue')

export default {
    name: 'UserSettingsEnvVars',
    mixins: [enumsData],
    components: { SettingsWrapper, CopyToClipboard, EnvVarCreateDialog },
    data() {
        return {
            level: 0,
            fetchingEnvVars: false,
            envvars: [],
            search: '',
            selectedEnvVar: null,
            requestRunning: false,
            includeDeleted: false,
            envVarDialog: false,
            envVarFormIsValid: false,
            newEnvVarValue: '',
            rules: {
                required: value => !!value || 'This field is required',
                validChars: p => /^[a-zA-Z0-9_-]*$/g.test(p) || 'Please use only letters, numbers, underscores and dashes.',
                validFirstChar: p => /^[a-zA-Z_]/.test(p) || 'Name can only start with a letter or an underscore.'
            },
            itemsPerPageOptions: [25, 50, 100, -1],
            tableOptions: {
                page: 0,
                itemsPerPage: 25,
                pageStart: 1,
                pageStop: 25,
                itemsLength: 25
            }
        }
    },
    computed: {
        ...mapGetters('spaceStore', ['isSpaceAdmin', 'isSpaceArchived']),
        ...mapGetters('orgStore', ['isOrgManager']),
        ...mapState('userStore', ['isAccountManager']),
        headers() {
            return [
                { text: 'Name', align: 'left', value: 'name' },
                { text: 'Value', align: 'left', value: 'value' },
                { text: 'Last change at', align: 'right', value: 'creation_timestamp' },
                { text: 'Actions', align: 'right', value: 'actions', sortable: false }
            ]
        },
        paramForLevel() {
            const paramsForLevels = {
                0: `personal`,
                2: `spaces`,
                3: `orgs`
            }
            return paramsForLevels[this.level]
        },
        actions() {
            return {
                delete() {
                    return {
                        endpoint: srid => `/env_vars/personal/${srid}`,
                        success: `Variable successfully deleted.`,
                        error: `There was an error deleting the variable.`
                    }
                },
                patch() {
                    return {
                        endpoint: srid => `/env_vars/personal/${srid}`,
                        success: `Variable successfully updated.`,
                        error: `There was an error updating the variable.`
                    }
                },
                post(level) {
                    return {
                        endpoint: sid => (level === 'personal' ? `/env_vars/personal` : `/${level}/${sid}/env_vars`),
                        success: `Successfully added environment variable.`,
                        error: `There was an error saving the variable.`
                    }
                },
                get(level, includeDeleted) {
                    return {
                        endpoint: sid => (level === 'personal' ? `/env_vars/personal` : `/${level}/${sid}/env_vars?include_deleted=${includeDeleted}`),
                        error: `There was an error fetching environment variables.`
                    }
                }
            }
        }
    },
    methods: {
        openDeleteDialog(envvar) {
            this.selectedEnvVar = envvar
            this.$store.dispatch('showGlobalDialog', {
                dialogText: `Are you sure you would like to delete this variable: <em>${this.selectedEnvVar.name}</em>?`,
                dialogAction: ['delete', 'cancel'],
                dialogTitle: 'Confirm deletion'
            })
        },
        openEnvVarDialog(envvar = null) {
            this.selectedEnvVar = envvar
            this.envVarDialog = true
        },
        closeEnvVarDialog() {
            this.selectedEnvVar = null
            this.envVarDialog = false
        },
        async sendUpdateRequest() {
            await this.sendRequest('patch', this.selectedEnvVar.evid, { value: this.newEnvVarValue })
            this.selectedEnvVar = null
            this.envVarDialog = false
            this.newEnvVarValue = ''
            this.fetchEnvVars()
        },
        async sendDeleteRequest() {
            await this.sendRequest('delete', this.selectedEnvVar.evid)
            this.selectedEnvVar = null
            this.envVarDialog = false
            this.fetchEnvVars()
        },
        async sendRequest(method, id, body = {}) {
            this.requestRunning = method
            const axiosRequest = this.$axios[method]
            const level = this.paramForLevel
            const endpoint = this.actions[method](level, this.includeDeleted).endpoint(id)
            let response
            try {
                response = await axiosRequest(endpoint, body)
                'success' in this.actions[method] &&
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: this.actions[method].success,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'check_circle'
                    })
            } catch (error) {
                console.log(this.actions[method])
                this.$store.dispatch('showSnackBar', {
                    snackBarText: error.response?.data?.debug || this.actions[method]().error,
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
            this.selectedEnvVar = null
            this.requestRunning = false
            return response
        },
        fetchEnvVars() {
            this.envvars = []
            const id = this.level === 2 ? this.$route.params.sid : this.$route.params.oid
            this.sendRequest('get', id).then(({ data }) => {
                this.envvars = data
            })
        },
        isDeleted: item => item.delete_timestamp && 'text--disabled',
        canModifyEnvVar(envvar) {
            if (envvar.delete_timestamp || envvar.env_var_type !== this.level) return false
            return this.canAddEnvVar()
        },
        canAddEnvVar() {
            if (this.level === 2) return this.isSpaceAdmin && !this.isSpaceArchived
            if (this.level === 3) return this.isOrgManager
            return this.level === 0
        }
    },
    created() {
        this.fetchEnvVars()
        eventBus.$on('global-dialog-accept', async () => {
            this.sendDeleteRequest()
        })
        eventBus.$on('env-var-created', ({ level, entityId }) => {
            if (Number(level) !== Number(this.level)) {
                return
            }
            if (this.level === 0) {
                this.fetchEnvVars()
                return
            }

            const currentEntityId = this.level === 2 ? this.$route.params.sid : this.$route.params.oid
            if (String(entityId) === String(currentEntityId)) {
                this.fetchEnvVars()
            }
        })
    },
    watch: {
        includeDeleted() {
            this.fetchEnvVars()
        }
    }
}
</script>
